var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import { Vectors } from "@project-rouge/rg-three";
import { Vector3 } from "three";
import { defaultAggProps } from "./index233.js";
import { Side } from "./index242.js";
const tol = 1e-5;
const _startToEnd = new Vector3();
class ThreadData {
  constructor(props) {
    __publicField(this, "context");
    __publicField(this, "size", 0);
    __publicField(this, "bidirectional");
    __publicField(this, "boundary");
    __publicField(this, "holes");
    __publicField(this, "dir");
    __publicField(this, "nDir");
    __publicField(this, "start");
    __publicField(this, "end");
    /** right direction */
    __publicField(this, "right");
    __publicField(this, "maxWingLength");
    /** on thread creation site points from initial start */
    __publicField(this, "sidePoints");
    __publicField(this, "startThread");
    __publicField(this, "endThread");
    /** `undefined` means it is double loaded */
    __publicField(this, "load");
    __publicField(this, "depth");
    __publicField(this, "depthRight");
    __publicField(this, "depthLeft");
    /** How much did this branch shrunk to allow another branch (if using grid).
     *
     * Used only by branches that branched, not by main thread.
     */
    __publicField(this, "shrinkDiff", 0);
    this.context = props.context;
    this.bidirectional = props.bidirectional;
    this.holes = props.holes;
    this.boundary = props.boundary;
    this.load = props.load;
    this.start = props.start;
    this.end = this.start.clone();
    this.dir = props.dir;
    this.nDir = this.dir.clone().negate();
    this.right = this.dir.clone().cross(Vectors.Up);
    this.maxWingLength = props.maxWingLength ?? defaultAggProps.maxWingLength;
    this.depth = this.isSingleLoad ? this.context.singleLoadDepth : this.context.doubleLoadDepth;
    const { left, right } = ThreadData.getDepths(this.context, props.load);
    this.depthLeft = left;
    this.depthRight = right;
    this.sidePoints = [
      this.start.clone().addScaledVector(this.right, -this.depthLeft),
      this.start.clone().addScaledVector(this.right, this.depthRight)
    ];
  }
  get isSingleLoad() {
    return !!this.load;
  }
  get isMain() {
    return !!this.bidirectional;
  }
  /** assumes `thread` originated from `this` */
  save({ data, start }) {
    let right = data.startThread.right;
    if (start)
      right = !right;
    if (start)
      this.startThread = { thread: data, right };
    else
      this.endThread = { thread: data, right };
  }
  toJSON() {
    throw new Error("not implemented");
  }
  fromJSON() {
    throw new Error("not implemented");
  }
  /** get left and right depths based on load type */
  static getDepths(context, load) {
    if (!load) {
      return {
        left: context.doubleLoadDepth * 0.5,
        right: context.doubleLoadDepth * 0.5
      };
    }
    const isRight = load === Side.right;
    return {
      left: !isRight ? context.doubleLoadDepth * 0.5 : context.corridorDepth * 0.5,
      right: isRight ? context.doubleLoadDepth * 0.5 : context.corridorDepth * 0.5
    };
  }
  /** gets the main thread for grow size calculations */
  getMainBranch() {
    var _a, _b;
    if ((_a = this.startThread) == null ? void 0 : _a.thread.isMain)
      return this.startThread.thread;
    if ((_b = this.endThread) == null ? void 0 : _b.thread.isMain)
      return this.endThread.thread;
  }
  /** update `size` based on `start` and `end` points */
  updateSize() {
    this.size = _startToEnd.subVectors(this.end, this.start).dot(this.dir);
    return this.size;
  }
  /** shrink if this isn't main branch but is gonna become the middle wing of 3 wings */
  shrinkBranchToBranch() {
    if (!this.context.gridCellWidth)
      return;
    if (this.shrinkDiff)
      return;
    if (this.isMain)
      return;
    const mainDepth = this.startThread.thread.depth;
    const corners = this.context.loadToCornerCoreDepth + this.context.loadToCornerCoreWidth;
    const innerMuscle = this.size + mainDepth - corners;
    this.shrinkDiff = Math.min(-(innerMuscle % this.context.gridCellWidth), 0);
    this.end.addScaledVector(this.dir, this.shrinkDiff);
    this.size += this.shrinkDiff;
  }
  /** undo `shrinkBranchToBranch` */
  unshrinkBranchToBranch() {
    if (!this.context.gridCellWidth)
      return;
    if (!this.shrinkDiff)
      return;
    if (this.isMain)
      return;
    this.end.addScaledVector(this.dir, -this.shrinkDiff);
    this.size -= this.shrinkDiff;
    this.shrinkDiff = 0;
  }
  /** shrink thread to grid. Only applicable to side wings */
  snapToGrid() {
    if (!this.context.gridCellWidth)
      return;
    if (this.startThread && this.endThread)
      return;
    const branch = this.startThread || this.endThread;
    if (!branch)
      return;
    const size = this.size + (this.bidirectional ? 0 : branch.thread.depth) - tol;
    const start = !!this.startThread;
    const right = branch.right;
    const corner = start === right ? this.context.loadToCornerCoreWidth : this.context.loadToCornerCoreDepth;
    const diff = (size - corner - this.context.facadeWidth + this.context.gap * 0.5) % this.context.gridCellWidth;
    const point = start ? this.end : this.start;
    const dir = start ? this.dir : this.nDir;
    point.addScaledVector(dir, -diff);
    this.size -= diff;
  }
  static getThreadContext(dataset) {
    const minAboveEntrance = Math.min(...dataset.apartments.aboveEntrance.map((a) => a.size[0]));
    const smallestFlat = Math.min(...dataset.apartments.main.map((a) => a.size[0]));
    const { coreWithLifts, moduleDepth, cornerCore, corridorWidth, gridCellWidth, gap } = dataset.aggConfig;
    const minApartmentWidth = Math.min(...dataset.apartments.all.map((a) => a.size[0]));
    const maxApartmentDepth = dataset.aggConfig.maxApartmentDepth;
    const balconyDepth = maxApartmentDepth - moduleDepth;
    const facadeId = dataset.modulousIds.facadeId;
    const facadeData = facadeId ? dataset.rvData.find((d) => d.modulousId === facadeId) : void 0;
    const facadeWidth = (facadeData == null ? void 0 : facadeData.totalThickness) || 0;
    const context = {
      doubleLoadDepth: maxApartmentDepth * 2 + corridorWidth,
      singleLoadDepth: maxApartmentDepth + corridorWidth + facadeWidth,
      corridorDepth: corridorWidth,
      /** the face2face distance is the distance between threads and therefore
       * balcony depths need accounting for
      */
      face2face: dataset.aggConfig.face2FaceSeparation - balconyDepth + facadeWidth,
      face2side: dataset.aggConfig.face2FaceSeparation,
      // @todo use face2SideSeparation
      minApartmentWidth: minApartmentWidth + gap * 2,
      minLineBuilding: minAboveEntrance + gap + coreWithLifts.size[0] + facadeWidth * 2,
      // `- balconyDepth` to account for branch extra thickness
      minBranchLength: minAboveEntrance - balconyDepth + facadeWidth,
      min1CornerWing: minAboveEntrance + gap,
      min2CornerWing: minAboveEntrance + 2 * (gap + moduleDepth),
      singleSmallSide: corridorWidth * 0.5 + facadeWidth,
      singleBigSide: corridorWidth * 0.5 + maxApartmentDepth,
      canTurnMinSize: maxApartmentDepth * 2 + corridorWidth + smallestFlat + gap * 3,
      gridCellWidth,
      loadToCornerCoreWidth: maxApartmentDepth + corridorWidth + cornerCore.size[0],
      loadToCornerCoreDepth: maxApartmentDepth + corridorWidth + cornerCore.size[1],
      gap,
      facadeWidth
    };
    return context;
  }
}
export {
  ThreadData
};
//# sourceMappingURL=index197.js.map
